import type { ReactNode } from 'react';
import { Icon, type IconName } from '../theme/Icon';
import { t } from '@app/i18n';
import { InfoTip } from './InfoTip';
import s from './Badge.module.css';

export type BadgeTone = 'danger' | 'warning' | 'success' | 'primary' | 'neutral';

export interface BadgeProps {
  tone?: BadgeTone;
  icon?: IconName;
  /** Explicação ⓘ ao lado do badge — obrigatória em linha de status (FR-132). */
  tip?: string;
  className?: string;
  children: ReactNode;
}

/** Badge (`.badge`) — estado curto e forte. */
export function Badge({ tone = 'neutral', icon, tip, className, children }: BadgeProps) {
  return (
    <>
      <span className={[s.badge, s[tone], className].filter(Boolean).join(' ')} data-tone={tone}>
        {icon ? <Icon name={icon} size="xs" /> : null}
        {children}
      </span>
      {tip ? <InfoTip text={tip} label={t('common.ui.about.state')} size="xs" /> : null}
    </>
  );
}

export type Severidade = 'critical' | 'high' | 'medium' | 'low' | 'minimum';

/**
 * Rótulo da severidade, resolvido em tempo de desenho. Era um `Record` de
 * módulo — avaliado uma vez, no import — que congelava o idioma da primeira
 * renderização (qa-i18n, 09/09).
 */
function severityLabel(severidade: Severidade): string {
  return t(`common.ui.severity.${severidade}`);
}

export interface SeverityDotProps {
  severidade: Severidade;
  /** Texto no círculo. Padrão: a inicial do rótulo. */
  children?: ReactNode;
}

/** Severidade em círculo (`.sev`) — risco de identidade/decisão. */
export function SeverityDot({ severidade, children }: SeverityDotProps) {
  return (
    <span
      className={[s.sev, s[severidade]].join(' ')}
      role="img"
      aria-label={severityLabel(severidade)}
      data-severidade={severidade}
    >
      {children ?? severityLabel(severidade)[0]}
    </span>
  );
}
